import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Button,
  Grid,
  CircularProgress,
  Divider,
  Typography,
  TextField,
  Box,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Select,
  MenuItem,
  ListSubheader,
  Checkbox,
  Alert,
} from '@mui/material';

import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import UploadIcon from '@mui/icons-material/Upload';
import PreviewIcon from '@mui/icons-material/Preview';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';

import { dispatchException, dispatchMessage } from 'helper/snackbar';

import { CustomTabsControlled } from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';

import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';

import {
  EVICT_INVOICE_QUERIES,
  INVOICE_APPROVE_CLASSIC_HOTEL_MUTATION,
  REFETCH_INVOICE_QUERIES,
  LIST_INVOICE_COSTCENTRES_QUERY,
  INVOICE_GENERATE_MUTATION,
  INVOICE_VIEW_OFFERVERSIONMEDIA_QUERY,
  INVOICE_VIEW_OFFERMEDIA_QUERY,
  INVOICE_APPROVE_CLASSIC_SEM_MUTATION,
} from './gql';

import {
  ECountryCode,
  EOfferCommissionBase,
  EOfferMediaType,
  EOfferSource,
  EOfferVersionContentBlockType,
  EOfferVersionStatus,
  ViewInvoiceQuery,
  WLOfferInvoiceViewFragment,
  WLOfferMedia,
} from '__generated__/graphql';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import yup from 'validation';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatPrice } from 'components/Price';
import { calculateTotals, getFullEventDayRange } from 'semshared/offer/offer';
import { FormInputText } from 'components/form/FormInputText';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputCurrency } from 'components/form/FormInputCurrency';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { FormInputCountryDropdown, FormInputDropdown } from 'components/form/FormInputDropdown';
import FileUploadButton from 'components/FileUploadButton';
import { ProductTextSelector, ProductTextSelectorType } from 'semshared/pricelist/producttextselector';
import InformationDialog from 'components/dialogs/InformationDialog';
import { exportBase64ToFile, getFullBackendUrl } from 'helper/download';
import { formatPercentage } from 'components/Percentage';
import { userSelector } from 'helper/security';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import SimpleDialog from 'components/dialogs/Dialog';
import OfferHistoryTable from 'components/OfferHistoryTable';
import BookingTasksList from 'components/BookingTasksList';
import ContactPersonTabsWrapper from 'components/ContactPersonTabsWrapper';
import { getDefaultCommissionRate, ICommissionRate } from 'semshared/offer/commission';
import { NO_PRICE } from 'semshared/pricelist/quickprice';
import { roundPrice } from 'semshared/utils/round';
import { formatOfferDay } from 'semshared/utils/format';
import { formatDate } from 'components/DateTime';
import { QuickPriceCalculatorTypeLister } from 'semshared/pricelist/quickprice_lister';
import { FormInputPercentage } from 'components/form/FormInputPercentage';
import InvoiceCancellationFormHotel from './invoice_cancellation';

interface InvoiceClassicFormProps {
  offer: NonNullable<ViewInvoiceQuery['viewWlOffer']>;
  version: NonNullable<ViewInvoiceQuery['viewWlOfferInvoiceVersion']>;
  downloadVersion: ViewInvoiceQuery['viewWlOfferInvoiceDownloadVersion'];
  event: ViewInvoiceQuery['viewWlOfferInvoiceEvent'];
  quickPriceCalculator: QuickPriceCalculatorTypeLister;
}

function getInvoiceConfirmationDownloadLabel(downloadVersion: InvoiceClassicFormProps['downloadVersion']) {
  return downloadVersion?.status === EOfferVersionStatus.CANCELLED_CLIENT
    ? i18next.t('offer-cancellation-confirmation-process-download')
    : i18next.t('offer-confirmation-process-download');
}

function InvoiceClassicReadonlyBookedServices({
  version,
  quickPriceCalculator,
}: {
  version: NonNullable<ViewInvoiceQuery['viewWlOfferInvoiceVersion']>;
  quickPriceCalculator: QuickPriceCalculatorTypeLister;
}) {
  const dayBlocks = version.contentBlocks
    .filter(cb => cb.type === EOfferVersionContentBlockType.LINE_ITEMS && cb.lineItems.length > 0)
    .slice()
    .sort((a, b) => a.lineItems[0].day - b.lineItems[0].day);

  if (dayBlocks.length === 0) return null;

  return (
    <>
      <Grid item xs={12} sx={{ pb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>{i18next.t('invoice-booked-services-readonly-title')}</Typography>
      </Grid>
      {dayBlocks.map((cb, cbIndex) => (
        <Grid item xs={12} key={`classic_readonly_day_${cbIndex}`} sx={{ pt: '6px !important' }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                {formatOfferDay(cb.lineItems[0].day)} - {formatDate(moment.utc(version.startDate, 'YYYY-MM-DD').add(cb.lineItems[0].day, 'days').toDate())} ({cb.lineItems.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                {cb.lineItems.map((li, liIndex) => (
                  <React.Fragment key={`classic_readonly_li_${cbIndex}_${liIndex}`}>
                    <Grid item sm={1}>
                      <TextField label={i18next.t('invoice-lineitem-count')} value={li.count} disabled fullWidth />
                    </Grid>
                    <Grid item sm={7}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography>
                            {li.header} ({li.sku})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 2 }}>
                          <Grid container rowSpacing={2} columnSpacing={1.5}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label={i18next.t('invoice-lineitem-header')}
                                value={li.header}
                                disabled
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label={i18next.t('invoice-lineitem-details')}
                                value={li.details}
                                disabled
                                fullWidth
                                multiline
                                rows={3}
                              />
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid item sm={2}>
                      {li.components.length > 1 && (
                        <Grid container rowSpacing={2.5}>
                          {li.components.map((c, cIndex) => (
                            <Grid item sm={12} key={cIndex} sx={{ mb: 0.5 }}>
                              <TextField
                                label={`${quickPriceCalculator.getTaxRateForType(c.type.id)?.name || c.type.name} ${i18next.t(`invoice-lineitem-suffix-${version.priceList.isPricesNet ? 'net' : 'gross'}`)}`}
                                value={formatPrice(c.price, version.priceList.currency)}
                                disabled
                                fullWidth
                              />
                            </Grid>
                          ))}
                        </Grid>
                      )}
                      {li.components.length <= 1 && (
                        <TextField
                          label={version.priceList.isPricesNet ? i18next.t('invoice-lineitem-price-net') : i18next.t('invoice-lineitem-price-gross')}
                          value={formatPrice(li.priceItem, version.priceList.currency)}
                          disabled
                          fullWidth
                        />
                      )}
                    </Grid>
                    <Grid item sm={1}>
                      <TextField
                        label={i18next.t('invoice-lineitem-net')}
                        value={formatPrice(li.priceNet, version.priceList.currency)}
                        disabled
                        fullWidth
                      />
                    </Grid>
                    <Grid item sm={1}>
                      <TextField
                        label={i18next.t('invoice-lineitem-gross')}
                        value={formatPrice(li.priceGross, version.priceList.currency)}
                        disabled
                        fullWidth
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ))}
      <Grid item xs={12} sx={{ pt: '6px !important' }}>
        <hr />
      </Grid>
    </>
  );
}

const addressSchema = yup.object().shape({
  businessName: yup.string().required().label(i18next.t('invoice-sender-name')),
  businessAddress1: yup.string().required().label(i18next.t('invoice-sender-address1')),
  businessAddress2: yup.string().nullable(),
  businessAddress3: yup.string().nullable(),
  businessAddress4: yup.string().nullable(),
  businessZip: yup.string().required().label(i18next.t('invoice-sender-zip')),
  businessCity: yup.string().required().label(i18next.t('invoice-sender-city')),
  businessCountry: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).required().label(i18next.t('invoice-sender-country')),
  billingUid: yup.string().required().label(i18next.t('invoice-sender-billing-uid')),
  businessEmail: yup.string().required().label(i18next.t('invoice-sender-email')),
  businessPhone: yup.string().nullable(),
  contactName: yup.string().required().label(i18next.t('invoice-sender-contact')),
  comment: yup.string().nullable(),
});

const addressSchemaHotel = addressSchema.concat(
  yup.object().shape({
    accepted: yup.boolean().required()
  })
);

const mediaSchema = yup.object().shape({
  dbId: yup.number().required(),
  mediaType: yup.mixed<EOfferMediaType>().oneOf(Object.values(EOfferMediaType)).required(),
  name: yup.string().required(),
  mimeType: yup.string().required(),
  base64: yup.string().nullable(),
  size: yup.number().required(),
});

const validationSchemaHotel = yup.object().shape({
  commissionBaseAmount: yup.number().required().label(i18next.t('invoice-commission-base-amount')).transform((value) => Number.isNaN(value) ? null : value),
  commissionAmountNet: yup.number().required().transform((value) => Number.isNaN(value) ? null : value),
  commissionAmountGross: yup.number().required().transform((value) => Number.isNaN(value) ? null : value),
  media: yup.array().required().of(mediaSchema),
  deleteMediaIds: yup.array().required().of(yup.number().required()),
  sender: addressSchemaHotel.required(),
});

const validationSchemaSEM = yup.object().shape({
  commissionBase: yup.mixed<EOfferCommissionBase>().oneOf(Object.values(EOfferCommissionBase)).required().label(i18next.t('invoice-commission-base')),
  commissionBaseAmount: yup.number().required().label(i18next.t('invoice-commission-base-amount')).transform((value) => Number.isNaN(value) ? null : value),
  commissionPercentage: yup.number().required().label(i18next.t('invoice-commission-percentage')).transform((value) => Number.isNaN(value) ? null : value),
  commissionAmountNet: yup.number().required().transform((value) => Number.isNaN(value) ? null : value),
  commissionAmountGross: yup.number().required().transform((value) => Number.isNaN(value) ? null : value),
  commissionRemark: yup.string().nullable(),
  sender: addressSchema.required(),
});

export type InvoiceFormHotelType = yup.InferType<typeof validationSchemaHotel>;
export type InvoiceFormSEMType = yup.InferType<typeof validationSchemaSEM>;
export type MediaFormType = yup.InferType<typeof mediaSchema>;

function InvoiceClassicFormHotel(props: InvoiceClassicFormProps & { onProcessStep: () => void; }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = userSelector()!;
  const offerSource = props.offer.source;

  const [approveInvoice, { loading: approveInvoiceLoading }] = useMutation(INVOICE_APPROVE_CLASSIC_HOTEL_MUTATION);
  const [offerMediaQuery] = useLazyQuery(INVOICE_VIEW_OFFERMEDIA_QUERY);

  const [selectedTab, setSelectedTab] = useState(0);
  const [offer] = useState(props.offer);
  const [sender] = useState(props.version.sender);
  const [version] = useState(props.version);

  const [hasRequiredMedias, setHasRequiredMedias] = useState(false);
  const [commission] = useState<ICommissionRate>(
    offer.commissionBase && offer.commissionPercentage ? {
      commissionBase: offer.commissionBase,
      commissionPercentage: offer.commissionPercentage,
    } : getDefaultCommissionRate(offer.hotel, offer.publicCompany, version.offerDate));

  const [canApprove] = useState(version.status === EOfferVersionStatus.IV_WAITFORHOTEL);
  const [canEdit] = useState(version.status === EOfferVersionStatus.IV_WAITFORHOTEL);

  const toFormSchema = (offer: typeof props.offer, version: typeof props.version): InvoiceFormHotelType => {
    const accepted = (version.sender && addressSchema.isValidSync(version.sender)) || false;

    return {
      commissionBaseAmount: offer.commissionBaseAmount || (commission.commissionBase === EOfferCommissionBase.NET ? version.totalPriceNet : version.totalPriceGross),
      commissionAmountNet: offer.commissionAmountNet || roundPrice((commission.commissionBase === EOfferCommissionBase.NET ? version.totalPriceNet : version.totalPriceGross) * commission.commissionPercentage),
      commissionAmountGross: offer.commissionAmountGross || roundPrice((commission.commissionBase === EOfferCommissionBase.NET ? version.totalPriceNet : version.totalPriceGross) * commission.commissionPercentage),
      media: offer.media.map(m => ({
        dbId: m.id,
        mediaType: m.mediaType,
        name: m.name,
        mimeType: m.mimeType,
        size: m.size,
      })),
      deleteMediaIds: [],
      sender: {
        businessName: version.sender?.businessName || '',
        businessAddress1: version.sender?.businessAddress1 || '',
        businessAddress2: version.sender?.businessAddress2,
        businessAddress3: version.sender?.businessAddress3,
        businessAddress4: version.sender?.businessAddress4,
        businessZip: version.sender?.businessZip || '',
        businessCity: version.sender?.businessCity || '',
        businessCountry: version.sender?.businessCountry || ECountryCode.AT,
        billingUid: version.sender?.billingUid || '',
        businessEmail: version.sender?.businessEmail || user.email,
        businessPhone: version.sender?.businessPhone,
        contactName: version.sender?.contactName || user.name,
        comment: version.sender?.comment,
        accepted: accepted,
      },
    };
  };
  const form = useForm({
    mode: 'all',
    resolver: yupResolver(validationSchemaHotel) as any,
    defaultValues: toFormSchema(props.offer, props.version),
  });
  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors: validationErrors, isValid, isDirty, isValidating, isSubmitting, dirtyFields },
  } = form;

  const {
    fields: mediaFields,
    append: mediaAppend,
    remove: mediaRemove,
  } = useFieldArray({
    control,
    name: 'media',
  });

  const mediasWatch = watch('media');
  useEffect(() => {
    const hasRequiredMedias = getValues('media').some(m => m.mediaType === EOfferMediaType.HOTEL_INVOICE)
    setHasRequiredMedias(hasRequiredMedias);
  }, [mediasWatch]);

  const commissionBaseAmountWatch = watch('commissionBaseAmount');

  useEffect(() => {
    setValue('commissionAmountNet', roundPrice(commissionBaseAmountWatch * commission.commissionPercentage));
    setValue('commissionAmountGross', roundPrice(commissionBaseAmountWatch * commission.commissionPercentage * 1.2));
  }, [commissionBaseAmountWatch]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  const [showMedia, setShowMedia] = useState<MediaFormType>()
  const [mediaBase64, setMediaBase64] = useState<string>();

  const onSubmit = async (values: InvoiceFormHotelType) => {
    if (!canApprove) return;

    try {
      const res = await approveInvoice({
        variables: {
          offerVersionId: props.version.id,
          files: values.media.filter(m => m.base64 && m.dbId === 0).map(m => ({
            mediaType: m.mediaType,
            name: m.name,
            mimeType: m.mimeType,
            base64: m.base64!,
            size: m.size,
          })),
          deleteMediaIds: values.deleteMediaIds,
          sender: {
            businessName: values.sender.businessName,
            businessAddress1: values.sender.businessAddress1,
            businessAddress2: values.sender.businessAddress2,
            businessAddress3: values.sender.businessAddress3,
            businessAddress4: values.sender.businessAddress4,
            businessZip: values.sender.businessZip,
            businessCity: values.sender.businessCity,
            businessCountry: values.sender.businessCountry,
            billingUid: values.sender.billingUid,
            businessEmail: values.sender.businessEmail,
            businessPhone: values.sender.businessPhone,
            comment: values.sender.comment,
            contactName: values.sender.contactName,
          },
          commissionBaseAmount: values.commissionBaseAmount,
          commissionAmountNet: values.commissionBaseAmount * commission.commissionPercentage
        },
        update: cache => {
          EVICT_INVOICE_QUERIES(cache);
        },
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_INVOICE_QUERIES(props.offer.id, props.version.id),
      });
      reset(toFormSchema(offer, res.data!.approveClassicInvoiceHotel));
      dispatchMessage(dispatch, i18next.t('invoice-status-approved'));
      props.onProcessStep();
      return res.data!.approveClassicInvoiceHotel.id;
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const setShowOfferVersionId = (versionId: number) => {
    navigate(`/offers/${props.offer.id}/${versionId}#editor`);
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t(`invoices-list-page-title-${props.offer.source}`), props.offer])}</title>
      </Helmet>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {props.version.extRefCode || props.offer.refCode} - {props.offer.bookingTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {props.version.status === 'CR_WAITFORHOTELAPPROVAL' && props.offer.status === 'APPROVED'
              ? i18next.t('enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION')
              : i18next.t(`enums-EOfferVersionStatus-${props.version.status}`)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {canApprove && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              confirmationQuestion={i18next.t('invoices-confirm-question')}
              confirmationTitle={i18next.t('invoices-confirm-question-title')}
              startIcon={isSubmitting ? <CircularProgress size={24} /> : <ThumbUpIcon />}
              disabled={!isValid || !setHasRequiredMedias || !watch('sender.accepted') || isSubmitting}
              onConfirm={async () => {
                const valid = await trigger();
                if (valid) {
                  await handleSubmit(onSubmit)();
                }
              }}
            >
              {i18next.t('invoices-confirm-title')}
            </ConfirmationButton>
          )}
          {props.downloadVersion && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              disabled={isSubmitting}
              onClick={() => {
                window.open(getFullBackendUrl(`/backend/pdf/downloadOffer/${props.downloadVersion!.id}`), '_blank');
              }}
            >
              {getInvoiceConfirmationDownloadLabel(props.downloadVersion)}
            </Button>
          )}
        </Grid>
        <Grid item xs={12}>
          <ContactPersonTabsWrapper responsibleUser={props.offer.responsibleUser}>
            <UnsavedChangesPrompt isDirty={isDirty} />
            <FormProvider {...form}>
              <CustomTabsControlled
                selected={selectedTab}
                handleChange={selected => setSelectedTab(selected)}
                headers={[
                  i18next.t('invoice-media-tab'),
                  i18next.t('invoice-info-tab'),
                  i18next.t('invoice-tasks-tab'),
                  i18next.t('offer-history-tab'),
                ]}
                hidden={[false, false, !(offerSource === EOfferSource.LISTER && (user.isSeminargo || user.isRoot)), false]}
                icons={[
                  ((canApprove && !hasRequiredMedias) ||
                    Object.keys(validationErrors).findIndex(v => v.startsWith('commission')) >= 0) ? (
                    <ErrorIcon />) : undefined,
                  ((canApprove && !watch('sender.accepted')) ||
                    Object.keys(validationErrors).findIndex(v => v.startsWith('sender')) >= 0) ? (
                    <ErrorIcon />
                  ) : undefined,
                  undefined,
                ]}
                tabs={[
                  <Grid container spacing={2}>
                    {[EOfferVersionStatus.IV_WAITFORHOTEL, EOfferVersionStatus.IV_WAITFORSEMAPPROVAL].includes(version.status) && (
                      <InvoiceClassicReadonlyBookedServices version={version} quickPriceCalculator={props.quickPriceCalculator} />
                    )}
                    {canEdit && <>
                      <Grid item xs={12}>
                        <Typography variant="h6">{i18next.t('invoice-media-upload-header')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1">{i18next.t('invoice-media-upload-description')}</Typography>
                      </Grid>
                    </>}
                    {!canEdit && <>
                      <Grid item xs={12}>
                        <Typography variant="h6">{i18next.t('invoice-media-upload-header-seminargo')}</Typography>
                      </Grid>
                    </>}
                    <Grid item xs={12}>
                      <SimpleTable
                        fullWidth={false}
                        rows={mediaFields.map((m, i) => ([
                          i18next.t(`invoice-media-type-${m.mediaType}`),
                          <a href="#"
                            onClick={() => {
                              if (m.base64) {
                                if (m.mimeType.startsWith('image/')) {
                                  setShowMedia(m);
                                  setMediaBase64(m.base64);
                                } else {
                                  exportBase64ToFile(m.base64, m.mimeType, m.name);
                                }
                              } else if (m.dbId > 0) {
                                offerMediaQuery({
                                  variables: {
                                    mediaId: m.dbId,
                                  },
                                }).then(res => {
                                  const base64 = res.data?.readWlOfferMediaBase64;
                                  if (base64) {
                                    if (m.mimeType.startsWith('image/')) {
                                      setShowMedia(m);
                                      setMediaBase64(base64);
                                    } else {
                                      exportBase64ToFile(base64, m.mimeType, m.name);
                                    }
                                  }
                                });
                              }
                            }}
                          >{m.name}</a>,
                          canEdit && <IconButton onClick={() => {
                            mediaRemove(i);
                            if (m.dbId > 0) {
                              setValue('deleteMediaIds', [...getValues('deleteMediaIds'), m.dbId]);
                            }
                          }}>
                            <DeleteIcon />
                          </IconButton>
                        ]))}
                      />
                      {showMedia &&
                        <InformationDialog title={showMedia.name} open={!!mediaBase64} onConfirm={() => { setMediaBase64(undefined); setShowMedia(undefined) }} maxWidth="md" fullWidth>
                          <Box p={2}>
                            <Grid container>
                              <Grid item xs={12}>
                                {mediaBase64 && <img width="100%" src={`data:${showMedia.mimeType};base64,${mediaBase64}`} />}
                              </Grid>
                            </Grid>
                          </Box>
                        </InformationDialog>
                      }
                    </Grid>
                    {canEdit && <>
                      <Grid item xs={12}>
                        <FileUploadButton
                          name="uploadInvoice"
                          accept={'image/*, application/pdf'}
                          disabled={!canEdit}
                          startIcon={<UploadIcon />}
                          binary={(name, type) => true}
                          onChange={async (name, mimeType, base64, size) => {
                            if (size > 10 * 1024 * 1024) {
                              dispatchException(dispatch, new Error(`${i18next.t('invoice-media-upload-helper-size')}`));
                              return;
                            }
                            mediaAppend({ dbId: 0, name, mimeType, base64, size, mediaType: EOfferMediaType.HOTEL_INVOICE });
                          }}
                        >
                          {i18next.t('invoice-media-upload-invoice')}
                        </FileUploadButton>
                        <FileUploadButton
                          name="uploadExtra"
                          accept={'image/*, application/pdf'}
                          disabled={!canEdit}
                          startIcon={<UploadIcon />}
                          binary={(name, type) => true}
                          onChange={async (name, mimeType, base64, size) => {
                            if (size > 10 * 1024 * 1024) {
                              dispatchException(dispatch, new Error(`${i18next.t('invoice-media-upload-helper-size')}`));
                              return;
                            }
                            mediaAppend({ dbId: 0, name, mimeType, base64, size, mediaType: EOfferMediaType.HOTEL_EXTRA });
                          }}
                        >
                          {i18next.t('invoice-media-upload-extra')}
                        </FileUploadButton>
                      </Grid>
                      <Grid item xs={12}>
                        {i18next.t('invoice-media-upload-helper')}
                      </Grid>
                    </>}
                    <Grid item xs={12}><hr /></Grid>
                    <Grid item xs={12}>
                      <FormInputCurrency
                        name="commissionBaseAmount"
                        control={control}
                        label={i18next.t('invoice-commission-base-amount')}
                        disabled={!canEdit || isSubmitting}
                        currency={version.priceList.currency}
                        helperText={commission.commissionBase === EOfferCommissionBase.NET ? i18next.t('invoice-commission-base-amount-net') : i18next.t('invoice-commission-base-amount-gross')}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {commission.commissionBase === EOfferCommissionBase.NET ?
                        i18next.t('invoice-commmission-info-text-net', { commissionPercentage: formatPercentage(commission.commissionPercentage), commissionAmountNet: formatPrice(getValues('commissionBaseAmount') * commission.commissionPercentage, version.priceList.currency), commissionBaseAmount: formatPrice(getValues('commissionBaseAmount'), version.priceList.currency) }) :
                        i18next.t('invoice-commmission-info-text-gross', { commissionPercentage: formatPercentage(commission.commissionPercentage), commissionAmountNet: formatPrice(getValues('commissionBaseAmount') * commission.commissionPercentage, version.priceList.currency), commissionBaseAmount: formatPrice(getValues('commissionBaseAmount'), version.priceList.currency) })}
                    </Grid>
                  </Grid>,
                  <Grid container spacing={2}>
                    <Grid item sm={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessName"
                            control={control}
                            label={i18next.t('invoice-sender-name')}
                            disabled={!canEdit || !!sender?.businessName || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessAddress1"
                            control={control}
                            label={i18next.t('invoice-sender-address1')}
                            disabled={!canEdit || !!sender?.businessAddress1 || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessAddress2"
                            control={control}
                            label={i18next.t('invoice-sender-address2')}
                            disabled={!canEdit || !!sender?.businessAddress2 || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormInputText
                            name="sender.businessZip"
                            control={control}
                            label={i18next.t('invoice-sender-zip')}
                            disabled={!canEdit || !!sender?.businessZip || isSubmitting}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={9}>
                          <FormInputText
                            name="sender.businessCity"
                            control={control}
                            label={i18next.t('invoice-sender-city')}
                            disabled={!canEdit || !!sender?.businessCity || isSubmitting}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputCountryDropdown
                            name="sender.businessCountry"
                            control={control}
                            label={i18next.t('invoice-sender-country')}
                            disabled={!canEdit || !!sender?.businessCountry || isSubmitting}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.billingUid"
                            control={control}
                            label={i18next.t('invoice-sender-billing-uid')}
                            disabled={!canEdit || !!sender?.billingUid || isSubmitting}
                            required
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item sm={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessEmail"
                            control={control}
                            label={i18next.t('invoice-sender-email')}
                            disabled={!canEdit || !!sender?.businessEmail || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessPhone"
                            control={control}
                            label={i18next.t('invoice-sender-phone')}
                            disabled={!canEdit || !!sender?.businessPhone || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.contactName"
                            control={control}
                            label={i18next.t('invoice-sender-contact')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.comment"
                            control={control}
                            label={i18next.t('invoice-sender-comment')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    {canEdit && canApprove && (
                      <Grid item xs={12} justifyItems={'end'}>
                        <FormInputCheckbox
                          name="sender.accepted"
                          label={i18next.t('invoice-sender-accepted')}
                          control={control}
                          disabled={isSubmitting || Object.keys(validationErrors).findIndex(v => v.startsWith('sender')) >= 0}
                        />
                      </Grid>
                    )}
                  </Grid>,
                  <BookingTasksList bookingId={props.offer.bookingId ?? props.offer.id} bookingRefCode={props.offer.refCode} bookingTitle={props.offer.bookingTitle ?? undefined} publicClientId={props.version.offer?.publicClient?.id} publicCompanyId={props.offer.publicCompany?.id} />,
                  <Grid container>
                    <Grid item xs={12}>
                      <OfferHistoryTable
                        history={props.offer.history}
                        emailHistory={props.offer.emailHistory}
                        canSeeClientData={true}
                        onOfferVersionClick={setShowOfferVersionId} />
                    </Grid>
                  </Grid>
                ]}
              />
            </FormProvider>
          </ContactPersonTabsWrapper>
        </Grid>
        <Grid item xs={12}>
          <hr />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">{i18next.t('invoice-totals')}</Typography>
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-net')}: {formatPrice(version.totalPriceNet, version.priceList.currency)}
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-taxes')}: {formatPrice(version.totalPriceGross - version.totalPriceNet, version.priceList.currency)}
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-gross')}: {formatPrice(version.totalPriceGross, version.priceList.currency)}
        </Grid>
      </Grid>
    </>
  );
}

function InvoiceClassicFormSEM(props: InvoiceClassicFormProps & { onProcessStep: () => void; }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = userSelector()!;
  const offerSource = props.offer.source;

  const [approveInvoice, { loading: approveInvoiceLoading }] = useMutation(INVOICE_APPROVE_CLASSIC_SEM_MUTATION);
  const [offerMediaQuery] = useLazyQuery(INVOICE_VIEW_OFFERMEDIA_QUERY);

  const [selectedTab, setSelectedTab] = useState(0);
  const [offer] = useState(props.offer);
  const [sender] = useState(props.version.sender);
  const [version] = useState(props.version);

  const [canApprove] = useState(user.isSeminargo && version.status === EOfferVersionStatus.IV_WAITFORSEMAPPROVAL);
  const [canEdit] = useState(user.isSeminargo && version.status === EOfferVersionStatus.IV_WAITFORSEMAPPROVAL);

  const toFormSchema = (offer: typeof props.offer, version: typeof props.version): InvoiceFormSEMType => {
    return {
      commissionBase: offer.commissionBase || EOfferCommissionBase.GROSS,
      commissionBaseAmount: offer.commissionBaseAmount || NaN,
      commissionAmountNet: offer.commissionAmountNet || NaN,
      commissionAmountGross: offer.commissionAmountGross || NaN,
      commissionPercentage: offer.commissionPercentage ? offer.commissionPercentage * 100 : NaN,
      commissionRemark: offer.commissionRemark,
      sender: {
        businessName: version.sender?.businessName || '',
        businessAddress1: version.sender?.businessAddress1 || '',
        businessAddress2: version.sender?.businessAddress2,
        businessAddress3: version.sender?.businessAddress3,
        businessAddress4: version.sender?.businessAddress4,
        businessZip: version.sender?.businessZip || '',
        businessCity: version.sender?.businessCity || '',
        businessCountry: version.sender?.businessCountry || ECountryCode.AT,
        billingUid: version.sender?.billingUid || '',
        businessEmail: version.sender?.businessEmail || user.email,
        businessPhone: version.sender?.businessPhone,
        contactName: version.sender?.contactName || user.name,
        comment: version.sender?.comment
      },
    };
  };
  const form = useForm({
    mode: 'all',
    resolver: yupResolver(validationSchemaSEM) as any,
    defaultValues: toFormSchema(props.offer, props.version),
  });
  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors: validationErrors, isValid, isDirty, isValidating, isSubmitting, dirtyFields },
  } = form;

  const commissionBaseWatch = watch('commissionBase');
  const commissionBaseAmountWatch = watch('commissionBaseAmount');
  const commissionPercentageWatch = watch('commissionPercentage');

  useEffect(() => {
    if (commissionBaseWatch === offer.commissionBase && offer.commissionBaseAmount) {
      setValue('commissionBaseAmount', offer.commissionBaseAmount);
    } else {
      if (commissionBaseWatch === EOfferCommissionBase.NET) {
        setValue('commissionBaseAmount', version.totalPriceNet);
      } else {
        setValue('commissionBaseAmount', version.totalPriceGross);
      }
    }
  }, [commissionBaseWatch]);

  useEffect(() => {
    setValue('commissionAmountNet', roundPrice(commissionBaseAmountWatch * commissionPercentageWatch));
    setValue('commissionAmountGross', roundPrice(commissionBaseAmountWatch * commissionPercentageWatch * 1.2));
  }, [commissionBaseAmountWatch, commissionPercentageWatch]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  const [showMedia, setShowMedia] = useState<WLOfferInvoiceViewFragment['media'][0] | undefined>()
  const [mediaBase64, setMediaBase64] = useState<string>();

  const onSubmit = async (values: InvoiceFormSEMType) => {
    if (!canApprove) return;

    try {
      const res = await approveInvoice({
        variables: {
          offerVersionId: props.version.id,
          sender: {
            businessName: values.sender.businessName,
            businessAddress1: values.sender.businessAddress1,
            businessAddress2: values.sender.businessAddress2,
            businessAddress3: values.sender.businessAddress3,
            businessAddress4: values.sender.businessAddress4,
            businessZip: values.sender.businessZip,
            businessCity: values.sender.businessCity,
            businessCountry: values.sender.businessCountry,
            billingUid: values.sender.billingUid,
            businessEmail: values.sender.businessEmail,
            businessPhone: values.sender.businessPhone,
            comment: values.sender.comment,
            contactName: values.sender.contactName,
          },
          commissionBase: values.commissionBase,
          commissionBaseAmount: values.commissionBaseAmount,
          commissionPercentage: values.commissionPercentage / 100,
          commissionRemark: values.commissionRemark,
        },
        update: cache => {
          EVICT_INVOICE_QUERIES(cache);
        },
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_INVOICE_QUERIES(props.offer.id, props.version.id),
      });
      reset(toFormSchema(offer, res.data!.approveClassicInvoiceSEM));
      props.onProcessStep();
      dispatchMessage(dispatch, i18next.t('invoice-status-erpconfirmed'));
      return res.data!.approveClassicInvoiceSEM.id;
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const setShowOfferVersionId = (versionId: number) => {
    navigate(`/offers/${props.offer.id}/${versionId}#editor`);
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t(`invoices-list-page-title-${props.offer.source}`), props.offer])}</title>
      </Helmet>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {props.version.extRefCode || props.offer.refCode} - {props.offer.bookingTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {props.version.status === 'CR_WAITFORHOTELAPPROVAL' && props.offer.status === 'APPROVED'
              ? i18next.t('enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION')
              : i18next.t(`enums-EOfferVersionStatus-${props.version.status}`)}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {canApprove && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              confirmationQuestion={i18next.t('invoices-erpconfirm-question')}
              confirmationTitle={i18next.t('invoices-erpconfirm-question-title')}
              startIcon={isSubmitting ? <CircularProgress size={24} /> : <ThumbUpIcon />}
              disabled={!isValid || isSubmitting}
              onConfirm={async () => {
                const valid = await trigger();
                if (valid) {
                  await handleSubmit(onSubmit)();
                }
              }}
            >
              {i18next.t('invoices-erpconfirm-title')}
            </ConfirmationButton>
          )}
          {props.downloadVersion && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              disabled={isSubmitting}
              onClick={() => {
                window.open(getFullBackendUrl(`/backend/pdf/downloadOffer/${props.downloadVersion!.id}`), '_blank');
              }}
            >
              {getInvoiceConfirmationDownloadLabel(props.downloadVersion)}
            </Button>
          )}
        </Grid>
        <Grid item xs={12}>
          <ContactPersonTabsWrapper responsibleUser={props.offer.responsibleUser}>
            <UnsavedChangesPrompt isDirty={isDirty} />
            <FormProvider {...form}>
              <CustomTabsControlled
                selected={selectedTab}
                handleChange={selected => setSelectedTab(selected)}
                headers={[
                  i18next.t('invoice-media-tab'),
                  i18next.t('invoice-info-tab'),
                  i18next.t('invoice-info-lineitems-tab'),
                  i18next.t('invoice-tasks-tab'),
                  i18next.t('offer-history-tab'),
                ]}
                hidden={[false, false, false, !(offerSource === EOfferSource.LISTER && (user.isSeminargo || user.isRoot)), false]}
                icons={[
                  (canEdit &&
                    Object.keys(validationErrors).findIndex(v => v.startsWith('commission')) >= 0) ? (
                    <ErrorIcon />) : undefined,
                  (canEdit &&
                    Object.keys(validationErrors).findIndex(v => v.startsWith('sender')) >= 0) ? (
                    <ErrorIcon />
                  ) : undefined,
                  undefined,
                  undefined
                ]}
                tabs={[
                  <Grid container spacing={2}>
                    {[EOfferVersionStatus.IV_WAITFORHOTEL, EOfferVersionStatus.IV_WAITFORSEMAPPROVAL].includes(version.status) && (
                      <InvoiceClassicReadonlyBookedServices version={version} quickPriceCalculator={props.quickPriceCalculator} />
                    )}
                    <Grid item xs={12}>
                      <Typography variant="h6">{i18next.t('invoice-media-upload-header-seminargo')}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <SimpleTable
                        fullWidth={false}
                        rows={offer.media.map((m, i) => ([
                          i18next.t(`invoice-media-type-${m.mediaType}`),
                          <a href="#"
                            onClick={() => {
                              offerMediaQuery({
                                variables: {
                                  mediaId: m.id,
                                },
                              }).then(res => {
                                const base64 = res.data?.readWlOfferMediaBase64;
                                if (base64) {
                                  if (m.mimeType.startsWith('image/')) {
                                    setShowMedia(m);
                                    setMediaBase64(base64);
                                  } else {
                                    exportBase64ToFile(base64, m.mimeType, m.name);
                                  }
                                }
                              });
                            }}
                          >{m.name}</a>,
                        ]))}
                      />
                      {showMedia &&
                        <InformationDialog title={showMedia.name} open={!!mediaBase64} onConfirm={() => { setMediaBase64(undefined); setShowMedia(undefined) }} maxWidth="md" fullWidth>
                          <Box p={2}>
                            <Grid container>
                              <Grid item xs={12}>
                                {mediaBase64 && <img width="100%" src={`data:${showMedia.mimeType};base64,${mediaBase64}`} />}
                              </Grid>
                            </Grid>
                          </Box>
                        </InformationDialog>
                      }
                    </Grid>
                    <Grid item xs={12}><hr /></Grid>
                    <Grid item xs={12} sm={4}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormInputDropdown
                            name="commissionBase"
                            control={control}
                            label={i18next.t('invoice-commission-base')}
                            disabled={!canEdit || isSubmitting}
                            options={Object.values(EOfferCommissionBase).map(cb => ({ label: i18next.t(`enums-EOfferCommissionBase-${cb}`), value: cb }))}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputCurrency
                            name="commissionBaseAmount"
                            control={control}
                            label={i18next.t('invoice-commission-base-amount')}
                            disabled={!canEdit || isSubmitting}
                            currency={version.priceList.currency}
                            helperText={watch('commissionBase') === EOfferCommissionBase.NET ? i18next.t('invoice-commission-base-amount-net') : i18next.t('invoice-commission-base-amount-gross')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputPercentage
                            name="commissionPercentage"
                            control={control}
                            label={i18next.t('invoice-commission-percentage')}
                            disabled={!canEdit || isSubmitting}
                            required
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                    </Grid>
                    <Grid item xs={12}>
                      {watch('commissionBase') === EOfferCommissionBase.NET ?
                        i18next.t('invoice-commmission-info-text-net', { commissionPercentage: formatPercentage(watch('commissionPercentage') / 100), commissionAmountNet: formatPrice(watch('commissionBaseAmount') * watch('commissionPercentage') / 100, version.priceList.currency), commissionBaseAmount: formatPrice(watch('commissionBaseAmount'), version.priceList.currency) }) :
                        i18next.t('invoice-commmission-info-text-gross', { commissionPercentage: formatPercentage(watch('commissionPercentage') / 100), commissionAmountNet: formatPrice(watch('commissionBaseAmount') * watch('commissionPercentage') / 100, version.priceList.currency), commissionBaseAmount: formatPrice(watch('commissionBaseAmount'), version.priceList.currency) })}
                    </Grid>
                  </Grid>,
                  <Grid container spacing={2}>
                    <Grid item sm={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessName"
                            control={control}
                            label={i18next.t('invoice-sender-name')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessAddress1"
                            control={control}
                            label={i18next.t('invoice-sender-address1')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessAddress2"
                            control={control}
                            label={i18next.t('invoice-sender-address2')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormInputText
                            name="sender.businessZip"
                            control={control}
                            label={i18next.t('invoice-sender-zip')}
                            disabled={!canEdit || isSubmitting}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={9}>
                          <FormInputText
                            name="sender.businessCity"
                            control={control}
                            label={i18next.t('invoice-sender-city')}
                            disabled={!canEdit || isSubmitting}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputCountryDropdown
                            name="sender.businessCountry"
                            control={control}
                            label={i18next.t('invoice-sender-country')}
                            disabled={!canEdit || isSubmitting}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.billingUid"
                            control={control}
                            label={i18next.t('invoice-sender-billing-uid')}
                            disabled={!canEdit || isSubmitting}
                            required
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item sm={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessEmail"
                            control={control}
                            label={i18next.t('invoice-sender-email')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.businessPhone"
                            control={control}
                            label={i18next.t('invoice-sender-phone')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.contactName"
                            control={control}
                            label={i18next.t('invoice-sender-contact')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormInputText
                            name="sender.comment"
                            control={control}
                            label={i18next.t('invoice-sender-comment')}
                            disabled={!canEdit || isSubmitting}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>,
                  <Grid container spacing={2}>
                    {version.contentBlocks.filter(cb => cb.type === EOfferVersionContentBlockType.LINE_ITEMS && cb.lineItems.length > 0).map((cb, index) => (
                      <Grid item xs={12} key={`lineitems_${index}`}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                              {formatOfferDay(cb.lineItems[0].day)} - {formatDate(moment.utc(version.startDate, 'YYYY-MM-DD').add(cb.lineItems[0].day, 'days').toDate())} ({(cb.lineItems || []).length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={1}>
                              {cb.lineItems.map((li, liIndex) => (<React.Fragment key={liIndex}>
                                <Grid item sm={1}>
                                  <TextField
                                    label={i18next.t('invoice-lineitem-count')}
                                    value={li.count}
                                    disabled
                                  />
                                </Grid>
                                <Grid item sm={7}>
                                  <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography>
                                        {li.header} ({li.sku})
                                      </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Grid container spacing={1}>
                                        <Grid item xs={12} sm={6}>
                                          <TextField
                                            label={i18next.t('invoice-lineitem-header')}
                                            value={li.header}
                                            disabled
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <TextField
                                            label={i18next.t('invoice-lineitem-details')}
                                            value={li.details}
                                            disabled
                                            multiline
                                            rows={3}
                                          />
                                        </Grid>
                                      </Grid>
                                    </AccordionDetails>
                                  </Accordion>
                                </Grid>
                                <Grid item sm={2}>
                                  {li.components.length > 1 && (
                                    <Grid container spacing={1}>
                                      {li.components.map((c, index) => (
                                        <Grid item sm={12} key={index}>
                                          <TextField
                                            label={`${props.quickPriceCalculator.getTaxRateForType(c.type.id)?.name || c.type.name} ${i18next.t(`invoice-lineitem-suffix-${version.priceList.isPricesNet ? 'net' : 'gross'}`)}`}
                                            value={formatPrice(c.price, version.priceList.currency)}
                                            disabled
                                          />
                                        </Grid>
                                      ))}
                                    </Grid>
                                  )}
                                  {li.components.length <= 1 && (
                                    <TextField
                                      label={version.priceList.isPricesNet ? i18next.t('invoice-lineitem-price-net') : i18next.t('invoice-lineitem-price-gross')}
                                      value={formatPrice(li.priceItem, version.priceList.currency)}
                                      disabled
                                    />
                                  )}
                                </Grid>
                                <Grid item sm={1}>
                                  <TextField
                                    label={i18next.t('invoice-lineitem-net')}
                                    value={formatPrice(li.priceNet, version.priceList.currency)}
                                    disabled
                                  />
                                </Grid>
                                <Grid item sm={1}>
                                  <TextField
                                    label={i18next.t('invoice-lineitem-gross')}
                                    value={formatPrice(li.priceGross, version.priceList.currency)}
                                    disabled
                                  />
                                </Grid>
                              </React.Fragment>
                              ))}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    ))}
                    {version.contentBlocks.filter(cb => cb.type === EOfferVersionContentBlockType.CANCELLATION_ITEMS && cb.lineItems.length > 0).map((cb, index) => (
                      <Grid item xs={12} key={`cancellationitems_${index}`}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>
                              {i18next.t('offerblock-cancellationitems-header-phase', {
                                rate: cb.cancellationRule ? formatPercentage(cb.cancellationRule.rate, 0) : '?',
                                daysToEvent: cb.cancellationRule ? cb.cancellationRule.daysToEvent : '?',
                              })}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={1}>
                              {cb.lineItems.map((li, liIndex) => (<React.Fragment key={liIndex}>
                                <Grid item sm={2}>
                                  {formatOfferDay(li.day)} - {formatDate(moment.utc(version.startDate, 'YYYY-MM-DD').add(li.day, 'days').toDate())}
                                </Grid>
                                <Grid item sm={1}>
                                  <TextField value={li.count} disabled />
                                </Grid>
                                <Grid item sm={8}>
                                  <Typography>
                                    {li.header} ({li.sku})
                                  </Typography>
                                </Grid>
                                <Grid item sm={1}>
                                  <TextField
                                    value={formatPrice(li.priceGross, version.priceList.currency)}
                                    disabled
                                  />
                                </Grid>
                              </React.Fragment>))}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    ))}
                  </Grid>,
                  <BookingTasksList bookingId={props.offer.bookingId ?? props.offer.id} bookingRefCode={props.offer.refCode} bookingTitle={props.offer.bookingTitle ?? undefined} publicClientId={props.version.offer?.publicClient?.id} publicCompanyId={props.offer.publicCompany?.id} />,
                  <Grid container>
                    <Grid item xs={12}>
                      <OfferHistoryTable history={props.offer.history} emailHistory={props.offer.emailHistory} canSeeClientData={true} onOfferVersionClick={setShowOfferVersionId} />
                    </Grid>
                  </Grid>
                ]}
              />
            </FormProvider>
          </ContactPersonTabsWrapper>
        </Grid>
        <Grid item xs={12}>
          <hr />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">{i18next.t('invoice-totals')}</Typography>
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-net')}: {formatPrice(version.totalPriceNet, version.priceList.currency)}
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-taxes')}: {formatPrice(version.totalPriceGross - version.totalPriceNet, version.priceList.currency)}
        </Grid>
        <Grid item xs={12}>
          {i18next.t('invoice-totals-gross')}: {formatPrice(version.totalPriceGross, version.priceList.currency)}
        </Grid>
      </Grid>
    </>
  );
}

export default function InvoiceClassicForm(props: InvoiceClassicFormProps) {
  const user = userSelector()!;

  const [stepId, setStepId] = useState(0);

  if (props.version.status === EOfferVersionStatus.CIV_WAITFORHOTEL)
    return <InvoiceCancellationFormHotel key={stepId} {...props} onProcessStep={() => setStepId(stepId + 1)} />;
  else if (!user.isSeminargo || props.version.status === EOfferVersionStatus.IV_WAITFORHOTEL)
    return <InvoiceClassicFormHotel key={stepId} {...props} onProcessStep={() => setStepId(stepId + 1)} />;
  else if (user.isSeminargo)
    return <InvoiceClassicFormSEM key={stepId} {...props} onProcessStep={() => setStepId(stepId + 1)} />;
  return null;
}
